import React from "react";

import { InputAdornment, Skeleton } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { classNames } from "repoV2/utils/common/render/classNames";
import { LISTING_TYPES } from "repoV2/features/Listing/Listing.constants";
import ExlyInput from "repoV2/features/Common/modules/Form/modules/ExlyInput/ExlyInput";
import ExlySearchableDropdown from "repoV2/features/Common/modules/Form/modules/ExlySearchableDropdown/ExlySearchableDropdown";
import { PAYMENT_METHOD_OPTIONS } from "repoV2/features/Payments/Payments.constants";
import CheckoutShimmer from "repoV2/features/CheckoutPages/modules/CheckoutShimmer/CheckoutShimmer";
import { Button } from "repoV2/components/common/Button";
import ExlySearchableDropdownV2 from "repoV2/features/Common/modules/Form/modules/ExlySearchableDropdownV2/ExlySearchableDrowpdownV2";
import { indian_states_and_uts_dropdown_options } from "repoV2/features/Common/modules/Location/constants/indianStatesUtsData";
import commonStyles from "repoV2/features/CheckoutPages/modules/CheckoutAccordion/CheckoutAccordion.module.scss";
import checkoutCommonStyles from "repoV2/features/CheckoutPages/CheckoutCommonStyles.module.scss";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import { useIsMounted } from "repoV2/utils/common/render/mount";
import { PAYOUT_GATEWAY } from "repoV2/constants/payment";
import { checkout_form } from "../../../../../CheckoutForm/CheckoutForm.constants";
import {
    useInternationalDetailsFormGroup,
    validators,
} from "./InternationalDetailsFormGroup.utils";
import { useUserDetailsFormContext } from "../UserDetailsFormContextProvider";
import { getUserFormFieldName } from "../../UserDetailsFormGroup.utils";
import styles from "./InternationalDetailsFormGroup.module.scss";
import useCheckShiprocketPinCode from "../../../MerchandiseDetailsFormGroup/utils/useCheckShiprocketPinCode";
import EstimatedDeliveryComponent from "../../../MerchandiseDetailsFormGroup/modules/EstimatedDeliveryComponent/EstimatedDeliveryComponent";

const {
    international_details: { field_names, placeholders },
} = checkout_form;

const payment_method_options = PAYMENT_METHOD_OPTIONS.map((option, idx) => ({
    id: `${option}-${idx}`,
    value: option,
    selectedLabel: option,
    label: option,
}));

const InternationalDetailsFormGroup = (props: {
    showPaymentMethodInputField: boolean;
    showInternationalInputFields: boolean;
    currencyName: string;
}) => {
    const {
        showPaymentMethodInputField,
        showInternationalInputFields,
        currencyName,
    } = props;

    const {
        setInternationalDetailsFormData,
        isSingleUser,
        setShiprocketPinCodeFormData,
        merchandiseStateValidator,
        setInternationalPaymentDetailsFormData,
        setMerchandiseStateFromData,
    } = useInternationalDetailsFormGroup({
        currencyName,
        showInternationalInputFields,
    });
    const { listingType, payoutGateway, isShiprocketEnabled } =
        useCheckoutBasicDetailsContext();

    const { userId } = useUserDetailsFormContext();

    /**
     * renderPaymentMethodInputField is true when:
     *
     * showPaymentMethodInputField: This is true only if the
     * creator is domestic and the customer is international one
     *
     * the customer is entering data for the first user
     *
     * payout gateway is not Tazapay because we only want user to make payment via tazapay when payout gateway is selected as Tazapay
     */
    const renderPaymentMethodInputField =
        showPaymentMethodInputField &&
        isSingleUser &&
        payoutGateway !== PAYOUT_GATEWAY.TAZAPAY;

    const isMounted = useIsMounted();

    const {
        isCheckingShiprocket,
        checkShiprocketPinCode,
        shiprocketData,
        shippingDate,
    } = useCheckShiprocketPinCode();

    if (!isMounted) {
        return <CheckoutShimmer />;
    }

    return (
        <div className={commonStyles.BasicForm}>
            {renderPaymentMethodInputField && (
                <div className={checkoutCommonStyles.dropdownBorderStyles}>
                    <ExlySearchableDropdown
                        name={field_names.payment_method}
                        options={payment_method_options}
                        placeholder={placeholders.payent_method}
                        classes={{
                            textInput: checkoutCommonStyles.inputBaseText,
                            helperText: checkoutCommonStyles.dropdownHelperText,
                            placeholder: checkoutCommonStyles.placeholderText,
                            inputRoot: checkoutCommonStyles.selectInput,
                            listbox: checkoutCommonStyles.selectMenuStyle,
                        }}
                        clearOnFocus
                        registerOptions={{
                            validate: validators[field_names.payment_method],
                        }}
                        onChange={() => {
                            setInternationalPaymentDetailsFormData();
                        }}
                        ExpandIcon={<ExpandMore />}
                    />
                </div>
            )}
            <ExlyInput
                name={getUserFormFieldName(userId, field_names.street_address)}
                classes={{
                    helperText: checkoutCommonStyles.helperText,
                }}
                registerOptions={{
                    validate: validators[field_names.street_address],
                }}
                overrideInputProps={{
                    placeholder: placeholders.street_address,
                    fullWidth: true,
                    onBlur: () => {
                        setInternationalDetailsFormData({
                            fieldName: field_names.street_address,
                        });
                    },
                    InputProps: {
                        className: classNames(
                            checkoutCommonStyles.inputBaseText,
                            checkoutCommonStyles.inputBaseAreaText
                        ),
                    },
                    className: checkoutCommonStyles.inputCustomStyles,
                    multiline: true,
                    rows: 4,
                }}
            />
            <ExlyInput
                name={getUserFormFieldName(userId, field_names.town_city)}
                classes={{
                    helperText: checkoutCommonStyles.helperText,
                }}
                registerOptions={{
                    validate: validators[field_names.town_city],
                }}
                overrideInputProps={{
                    placeholder: placeholders.town_city,
                    autoComplete: "country-name",
                    fullWidth: true,
                    onBlur: () => {
                        setInternationalDetailsFormData({
                            fieldName: field_names.town_city,
                        });
                    },
                    InputProps: {
                        className: checkoutCommonStyles.inputBaseText,
                    },
                    className: checkoutCommonStyles.inputCustomStyles,
                }}
            />
            {showInternationalInputFields ? (
                <ExlyInput
                    name={getUserFormFieldName(userId, field_names.user_state)}
                    classes={{
                        helperText: checkoutCommonStyles.helperText,
                    }}
                    registerOptions={{
                        validate: validators[field_names.user_state],
                    }}
                    overrideInputProps={{
                        placeholder: placeholders.user_state,
                        fullWidth: true,
                        autoComplete: "country-name",
                        onBlur: () => {
                            setInternationalDetailsFormData({
                                fieldName: field_names.user_state,
                            });
                        },
                        InputProps: {
                            className: checkoutCommonStyles.inputBaseText,
                        },
                        className: checkoutCommonStyles.inputCustomStyles,
                    }}
                />
            ) : (
                // Todo: In merchandise checkout, please make state here! state dropdown here
                <div className={checkoutCommonStyles.dropdownBorderStyles}>
                    <ExlySearchableDropdownV2
                        name={getUserFormFieldName(
                            userId,
                            field_names.user_state
                        )}
                        options={indian_states_and_uts_dropdown_options}
                        placeholder={placeholders.user_state}
                        classes={{
                            textInput: checkoutCommonStyles.inputBaseText,
                            helperText: checkoutCommonStyles.dropdownHelperText,
                            placeholder: checkoutCommonStyles.placeholderText,
                            inputRoot: checkoutCommonStyles.selectInput,
                            listbox: checkoutCommonStyles.selectMenuStyle,
                        }}
                        registerOptions={{
                            validate: merchandiseStateValidator,
                        }}
                        onChange={option => {
                            setMerchandiseStateFromData({
                                fieldName: field_names.user_state,
                                value: option,
                            });
                        }}
                        ExpandIcon={<ExpandMore />}
                    />
                </div>
            )}
            {LISTING_TYPES.MERCHANDISE === listingType &&
            isShiprocketEnabled ? (
                <>
                    <ExlyInput
                        name={getUserFormFieldName(
                            userId,
                            field_names.postal_code
                        )}
                        classes={{
                            helperText: checkoutCommonStyles.helperText,
                        }}
                        registerOptions={{
                            validate: validators[field_names.postal_code],
                        }}
                        overrideInputProps={{
                            placeholder: placeholders.postal_code,
                            autoComplete: "postal-code",
                            fullWidth: true,
                            onBlur: () => {
                                setShiprocketPinCodeFormData({
                                    fieldName: field_names.postal_code,
                                });
                            },
                            InputProps: {
                                className: checkoutCommonStyles.inputBaseText,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {isCheckingShiprocket ? (
                                            <div
                                                className={
                                                    styles.processingCouponCode
                                                }
                                            >
                                                <Skeleton
                                                    variant="circular"
                                                    width={10}
                                                    height={10}
                                                />
                                                <Skeleton
                                                    variant="circular"
                                                    width={10}
                                                    height={10}
                                                />
                                                <Skeleton
                                                    variant="circular"
                                                    width={10}
                                                    height={10}
                                                />
                                            </div>
                                        ) : (
                                            <Button
                                                className={classNames(
                                                    styles.buttonBase,
                                                    styles.applyButton
                                                )}
                                                type="button"
                                                design="plain"
                                                onClick={() => {
                                                    checkShiprocketPinCode({
                                                        currencyName,
                                                        isInternationalEnabled:
                                                            showInternationalInputFields,
                                                    });
                                                }}
                                            >
                                                CHECK
                                            </Button>
                                        )}
                                    </InputAdornment>
                                ),
                            },
                            className: checkoutCommonStyles.inputCustomStyles,
                        }}
                    />
                    <EstimatedDeliveryComponent
                        shippingDate={shippingDate}
                        showError={Boolean(shiprocketData?.error)}
                    />
                </>
            ) : (
                <ExlyInput
                    name={getUserFormFieldName(userId, field_names.postal_code)}
                    classes={{
                        helperText: checkoutCommonStyles.helperText,
                    }}
                    registerOptions={{
                        validate: validators[field_names.postal_code],
                    }}
                    overrideInputProps={{
                        placeholder: placeholders.postal_code,
                        autoComplete: "postal-code",
                        fullWidth: true,
                        onBlur: () => {
                            setInternationalDetailsFormData({
                                fieldName: field_names.postal_code,
                            });
                        },
                        InputProps: {
                            className: checkoutCommonStyles.inputBaseText,
                        },
                        className: checkoutCommonStyles.inputCustomStyles,
                    }}
                />
            )}
        </div>
    );
};

export default InternationalDetailsFormGroup;
